import { GlobalComponentService } from '@techmore/global';
import { LockDirective } from 'src/app/share/directive/lock-until.directive';
import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { BehaviorSubject, iif, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil, filter, tap, map, take } from 'rxjs/operators';
import { MemberCollection } from 'src/app/member-collections/member-collections/memberCollection';
import { UserService } from '../../service/user.service';
import { MemberProxyService } from 'src/app/member/service/member-proxy.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MEMBER_LOGIN_WAY } from 'src/app/member/entity/member-login-way';
import { OAuthError } from '../../entity/OauthError';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'theme-collect-btn',
  templateUrl: './theme-collect-btn.component.html',
  styleUrls: ['./theme-collect-btn.component.scss']
})
export class ThemeCollectBtnComponent implements OnInit, OnDestroy {
  @ViewChild('lock', { static: false }) lock: LockDirective;
  /** 主題流水號 | 收藏流水號 */
  @Input() id: number;
  /** 素材編號 */
  @Input() materialId: string;
  /** 素材/主題名稱 */
  @Input() title: string;
  /** 素材來源機關 CODE */
  @Input() fetchIndex: string;
  /** 連結模板或按鈕模板，預設是按鈕模板 */
  @Input() selectTmp: 'linkTmp' | 'btnTmp' = 'btnTmp';
  /** 連結模板的圖示是否要白色的，預設否 */
  @Input() whiteImg = false;
  /** 選擇要收藏資料還是主題，預設主題 */
  @Input() type: 'theme' | 'collection' = 'theme';
  /** 禁用 */
  @Input() disabled = false;
  /** 是否已收藏 */
  isCollected = false;
  /** 確認收藏狀態的方法 */
  checkCollectedFn;
  /** 確認收藏主題訂閱 */
  checkThemesSubc: Subscription;
  /** 確認收藏資料訂閱 */
  checkCollectionsSubc: Subscription;
  addToCollection$: Subject<void>;
  cancelCollection$: Subject<void>;
  ngUnSub$ = new Subject<void>();
  ngInnerUnSub$ = new Subject<void>();
  _isLoggedIn = new BehaviorSubject<boolean>(false);
  get isLoggedIn() { return this._isLoggedIn.getValue() };
  set isLoggedIn(value: boolean) { this._isLoggedIn.next(value); }
  /** 正在以某種方式登入會員中 */
  loginIng = false;

  constructor(
    private globalComponentService: GlobalComponentService,
    private memberProxyService: MemberProxyService,
    private userService: UserService,
    protected translateService: TranslateService,
  ) { }

  ngOnInit() {
    // 判斷目前網站是否有啟用會員功能，如果有則進一步判斷目前是否是登入狀態並用_isLoggedIn去監聽登入狀態
    this.memberProxyService.getHasLoginSystem$().pipe(
      filter(hasLoginSystem => hasLoginSystem),
      switchMap(() => this.memberProxyService.getMemberInfo$()),
      map((memberInfo) => !!memberInfo),
      takeUntil(this.ngUnSub$)
    ).subscribe(this._isLoggedIn);

    // 如果有登入則觀察登出的observable，如果登出時則重設收藏/打卡資訊以及將登入狀態設成false
    this._isLoggedIn.pipe(
      filter(isLoggedIn => isLoggedIn),
      tap(() => { this.subscribeCollectFlow() }),
      switchMap(() => this.memberProxyService.getLogout$()),
      takeUntil(this.ngUnSub$),
    ).subscribe(() => {
      this.isLoggedIn = false;
      this.isCollected = false;
      this.ngInnerUnSub$.next();
    });

    this.memberProxyService.getOnLoginStart$().subscribe(() => {
      this.loginIng = true;
    });

    this.memberProxyService.getOnLoginEnd$().subscribe(() => {
      this.loginIng = false;
    });
  }

  ngOnDestroy(): void {
    this.ngUnSub$.next();
    this.ngInnerUnSub$.next();
    if (this.checkCollectionsSubc) { this.checkCollectionsSubc.unsubscribe(); }
    if (this.checkThemesSubc) { this.checkThemesSubc.unsubscribe(); }
  }

  /** 訂閱處理流程 */
  subscribeCollectFlow() {
    if (this.type === 'theme') {
      // 取得收藏狀態
      this.checkCollectedFn = (collectedList) => {
        if (collectedList) {
          const f = collectedList.find(item => item.id === this.id);
          if (f) { this.isCollected = true; }
        } else {
          this.isCollected = false;
        }
      }

      if (!this.checkThemesSubc) {
        this.checkThemesSubc = this.userService.collectedTheme$.pipe(
          filter(list => list && !!list.length)
        ).subscribe(collectedTheme => this.checkCollectedFn(collectedTheme));
      }

      // 收藏主題流程
      this.addToCollection$ = new Subject<void>();
      this.addToCollection$.pipe(
        switchMap(() => this.userService.insertCollectedTheme(this.id)),
        switchMap(() => this.userService.getCollectedTheme()),
        takeUntil(this.ngInnerUnSub$)
      ).subscribe({
        next: d => {
          this.checkCollectedFn(d);
          this.lock.unlock();
        },
        error: () => {
          this.lock.unlock();
        },
        complete: () => {
          this.addToCollection$.unsubscribe();
          this.addToCollection$ = null;
        }
      });

      // 取消收藏主題流程
      this.cancelCollection$ = new Subject<void>();
      this.cancelCollection$.pipe(
        switchMap(() => this.userService.deleteMemberTheme(this.id)),
        switchMap(() => this.userService.getCollectedTheme()),
        takeUntil(this.ngInnerUnSub$)
      ).subscribe({
        next: d => {
          this.isCollected = false;
          this.lock.unlock();
        },
        error: () => {
          this.lock.unlock();
        },
        complete: () => {
          this.cancelCollection$.unsubscribe();
          this.cancelCollection$ = null;
        }
      });
    }

    if (this.type === 'collection') {
      // 取得收藏狀態
      this.checkCollectedFn = (collectedList: MemberCollection[]) => {
        if (collectedList) {
          const f = collectedList.find(item => (item.uid === this.materialId) && (item.sourceType === this.fetchIndex));
          if (f) {
            this.isCollected = true;
            this.id = f.id;
          }
        } else {
          this.isCollected = false;
        }
      }
      this.userService.collectedData$.pipe(take(1)).subscribe(collectedData => this.checkCollectedFn(collectedData));
      if (!this.checkCollectionsSubc) {
        this.checkCollectionsSubc = this.userService.collectedData$.pipe(
          filter(list => list && !!list.length)
        ).subscribe(collectedData => this.checkCollectedFn(collectedData));
      }
      // 收藏資料流程
      this.addToCollection$ = new Subject<void>();
      this.addToCollection$.pipe(
        switchMap(() => this.userService.insertCollectedData(this.fetchIndex, this.materialId)),
        switchMap(() => this.userService.getCollectedData()),
        takeUntil(this.ngInnerUnSub$)
      ).subscribe({
        next: d => {
          this.checkCollectedFn(d);
          this.userService.collectedData$.next(d);
          this.lock.unlock();
        },
        error: () => {
          this.lock.unlock();
        },
        complete: () => {
          this.addToCollection$.unsubscribe();
          this.addToCollection$ = null;
        }
      });

      // 取消收藏資料流程
      this.cancelCollection$ = new Subject<void>();
      this.cancelCollection$.pipe(
        switchMap(() => this.userService.deleteMemberData(this.id)),
        switchMap(() => this.userService.getCollectedData()),
        takeUntil(this.ngInnerUnSub$)
      ).subscribe({
        next: d => {
          this.isCollected = false;
          this.userService.collectedData$.next(d);
          this.lock.unlock();
        },
        error: () => {
          this.lock.unlock();
        },
        complete: () => {
          this.cancelCollection$.unsubscribe();
          this.cancelCollection$ = null;
        }
      });
    }
  }

  /** 收藏或取消收藏項目 */
  toggleCollect() {
    if (this.lock.isLock) { return; }
    if (this.loginIng) { return; }
    this.lock.lock();

    if (!this.isLoggedIn) {
      this.globalComponentService.openInfoAlert('請先登入', {
        afterClose: () => {
          this.openCultureCenterLogin();
        }
      });
      this.lock.unlock();
      return;
    }

    if (this.isCollected) {
      this.cancelCollection$.next();
    } else {
      this.addToCollection$.next();
    }
  }

  openCultureCenterLogin() {
    this.memberProxyService.login(MEMBER_LOGIN_WAY.CULTURE_CENTER).pipe(
      filter(v => v),
      switchMap(() => this._isLoggedIn)
    ).subscribe(v => {
      if (v) {
        this.toggleCollect();
      }
    }, error => {
      console.error(error);
      try {
        if (error instanceof HttpErrorResponse) {
          let oauthError: OAuthError;
          oauthError = $.extend(new OAuthError(this.translateService), error.error);
          this.globalComponentService.openErrorAlert(oauthError.getMessage());
        } else {
          this.globalComponentService.openErrorAlert(error.message);
        }
      } catch (e2) {
        this.globalComponentService.openErrorAlert(error.message);
      }
    });
  }
}
